import { faCalendar, faClock } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState, useCallback } from 'react';
import { formatFullTime, getCurrentTime, getNearestAvailableTime } from 'helpers/timeUtils';
import { scrollDate } from 'helpers/scrollDate';
import { CLOSE_TIME_LIMITS, RENT_TIME_LIMITS } from 'constants/rentTimeLimits';
import { DatePicker } from 'components/DatePicker';
import { TimePicker } from 'components/TimePicker';
import { Checkbox } from 'components/Checkbox';
import { useEntryFormContext } from 'context/EntryFormContext';
import styles from 'pages/Schedule/CreateEntry/CreateEntry.scss';

export const TimeRangePicker = () => {
  const { formMethods, formValues, isEditing, currentDate } = useEntryFormContext();
  const [isEndTimeDisabled, setIsEndTimeDisabled] = useState(false);
  const [minutesError, setMinutesError] = useState({});
  const [hoursError, setHoursError] = useState({});

  useEffect(() => {
    if (isEditing) {
      setIsEndTimeDisabled(!formValues.endTime);
    }
  }, [formValues.endTime, isEditing]);

  useEffect(() => {
    if (currentDate && currentDate !== formValues.date) {
      formMethods.setFieldValue('date', currentDate);
    }
  }, [currentDate, formMethods, formValues.date]);

  const handleDateChange = useCallback(
    (direction) => {
      const newDate = scrollDate(formValues.date, direction);
      formMethods.setFieldValue('date', newDate);
    },
    [formValues.date, formMethods]
  );

  const handleTimeChange = useCallback(
    ({ target: { name, value } }) => {
      formMethods.setFieldValue(name, value);
    },
    [formMethods]
  );

  const toggleEndTime = useCallback(
    (event) => {
      const isChecked = event?.target?.checked ?? false;

      setIsEndTimeDisabled(isChecked);

      if (isChecked) {
        formMethods.setFieldValue('endTime', null);
      } else {
        if (!formValues.endTime) {
          const currentTime = getCurrentTime(1);
          const defaultEndTime = formatFullTime(getNearestAvailableTime(currentTime, CLOSE_TIME_LIMITS));
          formMethods.setFieldValue('endTime', defaultEndTime);
        }
      }
    },
    [formValues.endTime, formMethods]
  );

  const getDefaultEndTime = useCallback(() => {
    if (formValues.endTime) return formValues.endTime;

    const currentTime = getCurrentTime(1);
    return formatFullTime(getNearestAvailableTime(currentTime, CLOSE_TIME_LIMITS));
  }, [formValues.endTime]);

  return (
    <div className={styles.box}>
      <div className={styles.formRow}>
        <div className={styles.column}>
          <div className={styles.label}>
            <div className={styles.icon}>
              <FontAwesomeIcon icon={faClock} />
            </div>
            Час
          </div>
          <div className={styles.time}>
            <TimePicker
              setTime={handleTimeChange}
              timeLimits={RENT_TIME_LIMITS}
              hoursError={hoursError}
              minutesError={minutesError}
              setMinutesError={setMinutesError}
              setHoursError={setHoursError}
              name='startTime'
              initialTime={formValues.startTime}
              key={`start-${formValues.startTime}`}
            />
            {isEndTimeDisabled ? (
              <>
                — <input className={styles.unknownTime} value='...' readOnly />
              </>
            ) : (
              <>
                —
                <TimePicker
                  setTime={handleTimeChange}
                  timeLimits={CLOSE_TIME_LIMITS}
                  hoursError={hoursError}
                  minutesError={minutesError}
                  setMinutesError={setMinutesError}
                  setHoursError={setHoursError}
                  name='endTime'
                  initialTime={getDefaultEndTime()}
                  key={`end-${formValues.endTime}`}
                />
              </>
            )}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            <div className={styles.icon}>
              <FontAwesomeIcon icon={faCalendar} />
            </div>
            Дата
          </div>
          <DatePicker
            className={styles.date}
            date={formValues.date}
            handleScrollDate={handleDateChange}
            enableCalendar
            showDay
          />
        </div>
      </div>
      <Checkbox
        className={styles.checkbox}
        label='Прибрати час закінчення'
        checked={isEndTimeDisabled}
        onChange={toggleEndTime}
      />
    </div>
  );
};
